import React from 'react';
import { Card } from '@/components/ui/card';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { ResponsiveContainer } from 'recharts';

export function ChartBox({ height = 260, testid, children }) {
  const ref = React.useRef(null);
  const [size, setSize] = React.useState({ w: 0, h: 0 });
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = Math.floor(e.contentRect.width);
        const h = Math.floor(e.contentRect.height);
        if (w > 0 && h > 0) setSize((p) => (p.w === w && p.h === h ? p : { w, h }));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ height }} className="w-full" data-testid={testid}>
      {size.w > 0 && size.h > 0 && (
        <ResponsiveContainer width={size.w} height={size.h} debounce={50}>
          {children}
        </ResponsiveContainer>
      )}
    </div>
  );
}

export const OPS_WEBHOOK_EVENTS = [
  'error.5xx',
  'security.lockout',
  'payment.stuck',
  'alert.error_rate',
  'alert.payment_backlog',
];

export function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="p-4 bg-card border border-border" data-testid={`ops-stat-${String(label).toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex justify-between items-center">
        <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${accent || 'text-[hsl(var(--primary))]'}`} />}
      </div>
      <div className="text-2xl font-display font-semibold tabular-nums mt-2">{value}</div>
      {sub && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{sub}</div>}
    </Card>
  );
}

export function Banner({ kind = 'error', title, message, action }) {
  const map = {
    error: 'border-red-500/40 bg-red-500/10 text-red-200',
    warn: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
    ok: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
  };
  const Icon = kind === 'ok' ? CheckCircle2 : AlertTriangle;
  return (
    <Card className={`p-4 border ${map[kind] || map.error}`}>
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          {title && <div className="font-semibold">{title}</div>}
          {message && <div className="text-sm mt-1 opacity-90 break-words">{message}</div>}
          {action && <div className="mt-3">{action}</div>}
        </div>
      </div>
    </Card>
  );
}

export function StatusPill({ status }) {
  const s = String(status || 'unknown').toLowerCase();
  const map = {
    up: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    ok: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    down: 'bg-red-500/15 text-red-300 border-red-500/30',
    disabled: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
    unknown: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] border ${map[s] || map.unknown}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s === 'up' || s === 'ok' ? 'bg-emerald-400' : s === 'down' ? 'bg-red-400' : 'bg-zinc-400'}`} />
      {status}
    </span>
  );
}

export function Loading({ label = 'Cargando...' }) {
  return (
    <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}
    </div>
  );
}

export function fmtDate(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

export function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-border/40 text-sm">
      <span className="text-[hsl(var(--muted-foreground))]">{k}</span>
      <span className="font-mono text-right break-all">{v === null || v === undefined || v === '' ? '\u2014' : String(v)}</span>
    </div>
  );
}
